class MobileGallery {
  constructor() {
    // ClassyTea: Removed width restriction - gallery works on all screen sizes
    // Desktop layout (≥1024px) uses side-by-side view
    window.mobileGalleryInstance = this; // For debugging access
    this.overlay = document.getElementById('mobileGallery');
    this.slider = document.getElementById('gallerySlider');
    this.counter = document.getElementById('galleryCounter');
    this.dotsContainer = document.getElementById('galleryDots');
    this.closeBtn = document.getElementById('closeGallery');
    this.currentIndex = 0;
    this.images = [];
    this.mediaContainers = [];
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.touchCount = 0;
    this.isOpen = false;
    this.isDragging = false;
    this.currentY = 0;
    this.startY = 0;
    this.historyStateAdded = false;
    this.sessionStartTime = null;
    this.slideViewTimes = new Map();
    this.currentSlideStartTime = null;
    this.highResLoaded = new Set();
    this.preloadQueue = [];
    this.isPreloading = false;
    this.isDesktopLayout = window.innerWidth >= 1024; // ClassyTea: Desktop detection
    this.init();
  }

  trackEvent(eventName, properties = {}) {
    if (window.LuckyOrange && typeof window.LuckyOrange.track === 'function') {
      try {
        window.LuckyOrange.track(eventName, {
          ...properties,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          screenSize: `${window.innerWidth}x${window.innerHeight}`
        });
      } catch (error) {
        console.warn('Lucky Orange tracking error:', error);
      }
    }
  }

  setupTouchActionWatcher() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
          const target = mutation.target;
          if (target === document.body || target === document.documentElement) {
            if (this.isOpen) {
              document.body.style.touchAction = 'manipulation';
              document.documentElement.style.touchAction = 'manipulation';
            }
          }
        }
      });
    });
    observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['style'] });
  }

  setupStaticATC() {
    const atcButton = document.getElementById('galleryATC');
    if (!atcButton) return;

    atcButton.addEventListener('click', (e) => {
      if (atcButton.disabled) return;
      
      this.trackEvent('Mobile Gallery ATC Clicked', {
        currentSlide: this.currentIndex + 1,
        totalSlides: this.mediaContainers.length,
        sessionDuration: this.getSessionDuration(),
        slideViewTime: this.getCurrentSlideViewTime()
      });
      
      atcButton.disabled = true;
      atcButton.innerHTML = '<span>Adding...</span>';
      
      const productForm = document.querySelector('product-form');
      if (productForm && productForm.submitButton) {
        productForm.submitButton.click();
        
        setTimeout(() => {
          if (!document.querySelector('[class*="error"]')) {
            this.trackEvent('Mobile Gallery ATC Success', {
              currentSlide: this.currentIndex + 1,
              totalSlides: this.mediaContainers.length,
              sessionDuration: this.getSessionDuration()
            });
            this.close();
          } else {
            this.trackEvent('Mobile Gallery ATC Error', {
              currentSlide: this.currentIndex + 1,
              totalSlides: this.mediaContainers.length
            });
            atcButton.disabled = false;
            atcButton.innerHTML = '<span>Add To Cart</span>';
          }
        }, 300);
      }
    });
  }

  init() {
    // ClassyTea: Move modal to body to escape stacking context issues
    if (this.overlay && this.overlay.parentElement !== document.body) {
      document.body.appendChild(this.overlay);
    }
    
    // ClassyTea: Removed width restriction - works on all screen sizes
    this.setupTouchActionWatcher();
    this.setupStaticATC();
    this.bindImageClicks();
    
    // ClassyTea: Desktop click-to-open
    if (this.isDesktopLayout) {
     this.bindDesktopClicks();
    }
    
    document.addEventListener('openMobileGallery', (e) => {
      const startIndex = e.detail?.startIndex || 0;
      this.open(startIndex);
    });
    
    this.closeBtn?.addEventListener('click', () => {
      this.trackEvent('Mobile Gallery Closed', {
        method: 'close_button',
        currentSlide: this.currentIndex + 1,
        totalSlides: this.mediaContainers.length,
        sessionDuration: this.getSessionDuration(),
        slideViewTimes: Object.fromEntries(this.slideViewTimes)
      });
      this.close();
    });
    
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.trackEvent('Mobile Gallery Closed', {
          method: 'overlay_click',
          currentSlide: this.currentIndex + 1,
          totalSlides: this.mediaContainers.length,
          sessionDuration: this.getSessionDuration(),
          slideViewTimes: Object.fromEntries(this.slideViewTimes)
        });
        this.close();
      }
    });
    
    this.slider?.addEventListener('touchstart', (e) => this.handleTouchStart(e), { passive: true });
    this.slider?.addEventListener('touchmove', (e) => this.handleTouchMove(e), { passive: false });
    this.slider?.addEventListener('touchend', (e) => this.handleTouchEnd(e), { passive: true });
    
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') {
        this.trackEvent('Mobile Gallery Closed', {
          method: 'escape_key',
          currentSlide: this.currentIndex + 1,
          totalSlides: this.mediaContainers.length,
          sessionDuration: this.getSessionDuration()
        });
        this.close();
      }
      if (e.key === 'ArrowLeft') {
        this.trackEvent('Mobile Gallery Navigation', {
          method: 'arrow_key',
          direction: 'previous',
          fromSlide: this.currentIndex + 1,
          toSlide: Math.max(1, this.currentIndex)
        });
        this.prev();
      }
      if (e.key === 'ArrowRight') {
        this.trackEvent('Mobile Gallery Navigation', {
          method: 'arrow_key',
          direction: 'next',
          fromSlide: this.currentIndex + 1,
          toSlide: Math.min(this.mediaContainers.length, this.currentIndex + 2)
        });
        this.next();
      }
    });

    window.addEventListener('popstate', (e) => {
      if (this.isOpen && !e.state?.galleryOpen) {
        this.trackEvent('Mobile Gallery Closed', {
          method: 'back_button',
          currentSlide: this.currentIndex + 1,
          totalSlides: this.mediaContainers.length,
          sessionDuration: this.getSessionDuration()
        });
        this.close();
      }
    });
    
    // ClassyTea: Updated resize handler - update layout mode on resize
    window.addEventListener('resize', () => {
      const wasDesktop = this.isDesktopLayout;
      this.isDesktopLayout = window.innerWidth >= 1024;
      
      if (this.isOpen) {
        // Update layout class if breakpoint crossed
        if (wasDesktop !== this.isDesktopLayout) {
          this.overlay.classList.toggle('desktop-layout', this.isDesktopLayout);
          this.repositionATCButton(); // ClassyTea: Move ATC button when crossing breakpoint
          this.updatePosition(); // ClassyTea: Recalculate slide position with correct units
        }
        // SAFARI FIX: Use setProperty with 'important' to override any CSS
        this.overlay.style.setProperty('width', window.innerWidth + 'px', 'important');
        this.overlay.style.setProperty('height', window.innerHeight + 'px', 'important');
      }
    });
    
    // ClassyTea: Disable momentum/inertia scrolling on PDP gallery
    this.disableMomentumScrolling();
  }
  
  // ClassyTea: Stop momentum scrolling on PDP gallery when user lifts finger
  disableMomentumScrolling() {
    // Target all possible gallery scrolling elements
    const selectors = [
      '.media-gallery .slideshow',
      '.media-gallery .slider--generic', 
      '.media-gallery',
      '.media-gallery__wrapper',
      '.product-page .media-gallery',
      '[data-media-gallery]'
    ];
    
    let mediaGallery = null;
    for (const selector of selectors) {
      mediaGallery = document.querySelector(selector);
      if (mediaGallery) {
        console.log('DEBUG: Found gallery element:', selector);
        break;
      }
    }
    
    if (!mediaGallery) {
      console.log('DEBUG: No gallery element found');
      return;
    }
    
    const stopMomentum = () => {
      const currentScrollTop = mediaGallery.scrollTop;
      const currentScrollLeft = mediaGallery.scrollLeft;
      mediaGallery.scrollTo({
        top: currentScrollTop,
        left: currentScrollLeft,
        behavior: 'instant'
      });
    };
    
    // Stop momentum on ANY touch (especially important for pinch gestures)
    mediaGallery.addEventListener('touchstart', (e) => {
      stopMomentum();
      
      // If 2 fingers, prevent browser zoom
      if (e.touches.length === 2) {
        e.preventDefault();
      }
    }, { passive: false, capture: true });
    
    // Also stop on touchend
    mediaGallery.addEventListener('touchend', stopMomentum, { passive: true });
    
    // Block browser zoom during touchmove with 2 fingers
    mediaGallery.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
      }
    }, { passive: false, capture: true });
    
    // ALSO add to document level as backup - capture phase
    document.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        const target = e.target;
        if (target.closest('.media-gallery')) {
          stopMomentum();
          e.preventDefault();
        }
      }
    }, { passive: false, capture: true });
    
    document.addEventListener('touchmove', (e) => {
      if (e.touches.length === 2) {
        const target = e.target;
        if (target.closest('.media-gallery')) {
          e.preventDefault();
        }
      }
    }, { passive: false, capture: true });
    
    console.log('DEBUG: Momentum scrolling disabled on PDP gallery');
  }

  // ClassyTea: Desktop click-to-open handler (only for ≥1024px, mouse only)
  bindDesktopClicks() {
    let lastTouchTime = 0;
    
    this.mediaContainers.forEach((container, index) => {
      container.style.cursor = 'pointer';
      
      // Track touch events to distinguish from mouse clicks
      container.addEventListener('touchstart', () => {
        lastTouchTime = Date.now();
      }, { passive: true });
      
      container.addEventListener('click', (e) => {
        // Don't trigger if clicking on interactive elements
        if (e.target.closest('button') || e.target.closest('a')) return;
        
        // Don't trigger if this was a touch event (within 500ms of touchstart)
        // This allows touch users to scroll/swipe without opening gallery
        if (Date.now() - lastTouchTime < 500) return;
        
        // Only trigger on actual mouse clicks (desktop)
        if (window.innerWidth < 1024) return;
        
        // Find current visible slide index
        const openIndex = this.detectCurrentSlideIndex(index);
        
        this.trackEvent('Mobile Gallery Opened', {
          method: 'desktop_click',
          startIndex: openIndex + 1
        });
        
        this.open(openIndex);
      });
    });
  }

  // ClassyTea: Detect current slide from aria-hidden or counter
  detectCurrentSlideIndex(fallbackIndex) {
    const allSlides = document.querySelectorAll('.slideshow__slide');
    const visibleSlides = [];

    allSlides.forEach((slide) => {
      const ariaHidden = slide.getAttribute('aria-hidden');
      const dataIndex = slide.dataset.index;
      if (ariaHidden === 'false' && dataIndex !== undefined) {
        visibleSlides.push({ dataIndex: parseInt(dataIndex) });
      }
    });

    if (visibleSlides.length === 1) {
      return visibleSlides[0].dataIndex;
    } else if (visibleSlides.length > 1) {
      return visibleSlides[visibleSlides.length - 1].dataIndex;
    }

    const counter = document.querySelector('.slider-counter--current');
    if (counter) {
      const counterIndex = parseInt(counter.textContent || '1') - 1;
      if (counterIndex >= 0 && counterIndex < this.mediaContainers.length) {
        return counterIndex;
      }
    }

    return fallbackIndex;
  }

  bindImageClicks() {
    console.log('DEBUG: Binding pinch-to-zoom trigger');
    const thumbnailContainers = document.querySelectorAll('.pdp-thumbnails__main');
    const slideshowContainers = document.querySelectorAll('.slideshow__slide');
    
    const allContainers = thumbnailContainers.length > 0 ? thumbnailContainers : slideshowContainers;
    
    this.mediaContainers = Array.from(allContainers).filter(container => {
      const hasImage = container.querySelector('img');
      const hasVideo = container.querySelector('deferred-media, [class*="deferred-media"]');
      return hasImage || hasVideo;
    });
    
    console.log(`Media containers found: ${allContainers.length}`);
    console.log(`Filtered containers: ${this.mediaContainers.length}`);
    
    this.images = this.mediaContainers.map(container => 
      container.querySelector('img')).filter(img => img);
    
    const containerToFilteredIndex = new Map();
    this.mediaContainers.forEach((container, filteredIndex) => {
      containerToFilteredIndex.set(container, filteredIndex);
    });
    
    // PINCH-ZOOM FIX: Block browser pinch-zoom on the entire media gallery
    // This prevents the browser from intercepting pinch gestures during carousel animation
    const mediaGallery = document.querySelector('.media-gallery .slideshow') || 
                         document.querySelector('.media-gallery') ||
                         document.querySelector('.slideshow');
    if (mediaGallery) {
      mediaGallery.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
        }
      }, { passive: false, capture: true });
      
      mediaGallery.addEventListener('touchmove', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
        }
      }, { passive: false, capture: true });
    }
    
    const self = this;
    allContainers.forEach((container) => {
      let initialPinchDistance = 0;
      let isPinching = false;
      let pinchTriggered = false;
      
      container.addEventListener('touchstart', (e) => {
        if (e.touches.length === 2) {
          e.preventDefault();
          initialPinchDistance = self.getPinchDistance(e.touches);
          isPinching = true;
          pinchTriggered = false;
        }
      }, { passive: false });
      
      container.addEventListener('touchmove', (e) => {
        // ClassyTea: Removed width restriction - pinch works on all screen sizes
        // ClassyTea: Added !self.isOpen check to prevent re-triggering while gallery is open
        if (e.touches.length === 2 && isPinching && !pinchTriggered && !self.isOpen) {
          e.preventDefault();
          const currentDistance = self.getPinchDistance(e.touches);
          const pinchDelta = currentDistance - initialPinchDistance;
          
          if (pinchDelta > 50) {
            pinchTriggered = true;
            isPinching = false;
            
            // FIXED: Use same detection logic as magnifying glass handler
            let openIndex = 0;
            
            // Try to find visible slides via aria-hidden
            const allSlides = document.querySelectorAll('.slideshow__slide');
            const visibleSlides = [];
            
            allSlides.forEach((slide) => {
              const ariaHidden = slide.getAttribute('aria-hidden');
              const dataIndex = slide.dataset.index;
              if (ariaHidden === 'false' && dataIndex !== undefined) {
                visibleSlides.push({dataIndex: dataIndex});
              }
            });
            
            if (visibleSlides.length === 1) {
              // Single visible slide - use it
              openIndex = parseInt(visibleSlides[0].dataIndex);
            } else if (visibleSlides.length > 1) {
              // Multiple visible (transition) - use LAST one (destination)
              openIndex = parseInt(visibleSlides[visibleSlides.length - 1].dataIndex);
            } else {
              // Fallback to counter
              const counter = document.querySelector('.slider-counter--current');
              if (counter) {
                const counterIndex = parseInt(counter.textContent || '1') - 1;
                if (counterIndex >= 0 && counterIndex < self.mediaContainers.length) {
                  openIndex = counterIndex;
                }
              }
              
              // Final fallback to container mapping
              if (openIndex === 0) {
                const filteredIndex = containerToFilteredIndex.get(container);
                if (filteredIndex !== undefined) {
                  openIndex = filteredIndex;
                }
              }
            }
            
            // DESKTOP LAYOUT FIX: Always reset browser zoom before opening modal
            self.resetZoomAndOpen(openIndex);
          }
        }
      }, { passive: false });
      
      container.addEventListener('touchend', () => {
        isPinching = false;
      }, { passive: true });
      
      container.addEventListener('touchcancel', () => {
        isPinching = false;
      }, { passive: true });
    });
  }
  
  getPinchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // ClassyTea: ALWAYS reset browser zoom before opening modal (for desktop layout pinch)
  resetZoomAndOpen(startIndex = 0) {
    // Prevent opening if already open
    if (this.isOpen) return;
    if (!this.overlay || this.mediaContainers.length === 0) return;
    
    const viewport = document.querySelector('meta[name="viewport"]');
    const self = this;
    
    // ALWAYS force zoom reset - don't check if zoomed, just reset
    if (viewport) {
      const originalContent = viewport.content;
      
      // Step 1: Force maximum-scale=1 to reset zoom
      viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
      
      // Step 2: Wait for browser to process the zoom reset
      setTimeout(() => {
        // Step 3: Restore original viewport (allow zooming again later)
        viewport.content = originalContent;
        
        // Step 4: Now open the modal with correct dimensions
        self.actualOpen(startIndex);
      }, 100); // Longer delay to ensure zoom is fully reset
    } else {
      // No viewport tag - just open directly
      this.actualOpen(startIndex);
    }
  }

  open(startIndex = 0) {
    // ClassyTea: Prevent opening if already open
    if (this.isOpen) return;
    
    if (!this.overlay || this.mediaContainers.length === 0) return;
    
    // FIX: Reset native browser zoom if page is zoomed
    // This handles the case where user pinch-zoomed during carousel animation
    if (window.visualViewport && window.visualViewport.scale > 1.01) {
      // Force zoom reset via viewport meta tag
      const viewport = document.querySelector('meta[name="viewport"]');
      if (viewport) {
        const originalContent = viewport.content;
        viewport.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
        
        // Wait for zoom reset, then open gallery
        const self = this;
        setTimeout(() => {
          // Restore original viewport settings
          viewport.content = originalContent;
          // Now open with correct dimensions
          self.actualOpen(startIndex);
        }, 50);
        return;
      }
    }
    
    this.actualOpen(startIndex);
  }
  
  actualOpen(startIndex) {
    this.currentIndex = startIndex;
    this.isOpen = true;
    this.sessionStartTime = Date.now();
    this.currentSlideStartTime = Date.now();
    
    // ClassyTea: Update desktop detection on open (in case of rotation)
    this.isDesktopLayout = window.innerWidth >= 1024;
    
    this.trackEvent('Mobile Gallery Opened', {
      startIndex: startIndex + 1,
      totalSlides: this.mediaContainers.length,
      hasVideos: this.mediaContainers.some(container => 
        container.querySelector('deferred-media, [class*="deferred-media"], external-media')
      ),
      imageCount: this.images.length,
      videoCount: this.mediaContainers.length - this.images.length,
      layout: this.isDesktopLayout ? 'desktop' : 'mobile' // ClassyTea: Track layout
    });

    if (window.LO && window.LO.events) {
      try {
        LO.events.track('Mobile Gallery Opened', {
          startSlide: startIndex + 1,
          totalSlides: this.mediaContainers.length,
          triggerSource: 'image_click'
        });
      } catch (error) {
        console.warn('Lucky Orange tracking error:', error);
      }
    }
    
    if (!this.historyStateAdded) {
      history.pushState({ galleryOpen: true }, '', window.location.href);
      this.historyStateAdded = true;
    }
    
    const windowHeight = window.innerHeight;
    const windowWidth = window.innerWidth;
    
    // SAFARI FIX: Use setProperty with 'important' to override any CSS
    this.overlay.style.setProperty('width', windowWidth + 'px', 'important');
    this.overlay.style.setProperty('height', windowHeight + 'px', 'important');
    
    const atcButton = document.getElementById('galleryATC');
    if (atcButton) {
      atcButton.disabled = false;
      atcButton.innerHTML = '<span>Add To Cart</span>';
    }
    
    this.populateSlider();
    this.createNavArrows();
    this.showZoomHint();
    this.populateFilmstrip();
    this.updateCounter();
    this.updatePosition();
    
    // Set up transition animation
    this.overlay.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
    this.overlay.style.opacity = '0';
    this.overlay.style.transform = 'scale(0.95)';
    
    // ClassyTea: Add desktop layout class if needed
    this.overlay.classList.toggle('desktop-layout', this.isDesktopLayout);
    
    // Add active class
    this.overlay.classList.add('active');
    
    // ClassyTea: Hide sticky ATC while gallery is open
    document.querySelector('.sticky-announcement-atc')?.classList.add('gallery-hidden');
    document.querySelector('.reveal-header')?.classList.add('gallery-hidden');
    
    // ClassyTea: Force display with !important to override any CSS rules
    this.overlay.style.setProperty('display', 'flex', 'important');
    
    // Force browser to recognize initial state
    this.overlay.offsetHeight;
    
    // Trigger animation on next frame
    requestAnimationFrame(() => {
      this.overlay.style.opacity = '1';
      this.overlay.style.transform = 'scale(1)';
    });
    
    document.body.style.overflow = 'hidden';
    document.body.style.overscrollBehavior = 'auto';
    document.body.style.setProperty('touch-action', 'manipulation', 'important');
    document.documentElement.style.setProperty('touch-action', 'manipulation', 'important');
    this.overlay.style.setProperty('touch-action', 'manipulation', 'important');
    this.overlay.focus();
    
    this.upgradeToHighRes(this.currentIndex);
    this.startPreloadQueue(this.currentIndex);
  }

  close() {
    if (!this.overlay) return;
    
    if (this.currentSlideStartTime) {
      const viewTime = Date.now() - this.currentSlideStartTime;
      this.slideViewTimes.set(this.currentIndex, 
        (this.slideViewTimes.get(this.currentIndex) || 0) + viewTime);
    }
    
    if (this.historyStateAdded) {
      this.historyStateAdded = false;
      if (window.history.state?.galleryOpen) {
        history.back();
      }
    }
    
    this.isOpen = false;
    
    const slides = this.slider?.querySelectorAll('.mobile-gallery-slide') || [];
    slides.forEach(slide => this.destroyLazyVideo(slide));
    
    // ClassyTea: Move ATC back to overlay before closing (reset to default position)
    const atcButton = document.getElementById('galleryATC');
    if (atcButton && atcButton.parentElement !== this.overlay) {
      this.overlay.appendChild(atcButton);
    }
    
    this.overlay.classList.remove('active');
    this.overlay.classList.remove('desktop-layout'); // ClassyTea: Remove desktop layout class
    
    // ClassyTea: Restore sticky ATC visibility
    document.querySelector('.sticky-announcement-atc')?.classList.remove('gallery-hidden');
    document.querySelector('.reveal-header')?.classList.remove('gallery-hidden');
    
    this.overlay.style.transform = '';
    this.overlay.style.opacity = '';
    this.overlay.style.display = ''; // ClassyTea: Reset display
    document.body.style.overflow = '';
    document.body.style.overscrollBehavior = '';
    document.body.style.setProperty('touch-action', 'manipulation', 'important');
    document.documentElement.style.setProperty('touch-action', 'manipulation', 'important');
  }

  getSessionDuration() {
    return this.sessionStartTime ? Date.now() - this.sessionStartTime : 0;
  }

  getCurrentSlideViewTime() {
    return this.currentSlideStartTime ? Date.now() - this.currentSlideStartTime : 0;
  }

  populateSlider() {
    if (!this.slider) return;
    this.slider.innerHTML = '';
    
    this.mediaContainers.forEach((container, index) => {
      const slide = document.createElement('div');
      slide.className = 'mobile-gallery-slide';
      
      const hasVideo = container.querySelector('deferred-media, [class*="deferred-media"], external-media');
      
      if (hasVideo) {
        const posterImg = container.querySelector('img');
        if (posterImg) {
          const image = document.createElement('img');
          image.className = 'mobile-gallery-image video-poster';
          image.src = this.detectPDPImageSize(posterImg);
          image.alt = posterImg.alt || `Product video ${index + 1}`;
          image.loading = index === this.currentIndex ? 'eager' : 'lazy';
          slide.appendChild(image);
        }
        
        slide.dataset.hasVideo = 'true';
        slide.dataset.videoIndex = index;
        this.storeVideoData(container, slide);
        
      } else {
        const imageElement = container.querySelector('img');
        if (imageElement) {
          const image = document.createElement('img');
          image.className = 'mobile-gallery-image';
          
          // SMART DETECTION: Use whatever size the PDP loaded
          const pdpLoadedSrc = this.detectPDPImageSize(imageElement);
          image.src = pdpLoadedSrc;
          image.dataset.highResSrc = this.getHighResUrl(imageElement.src);
          image.alt = imageElement.alt || `Product image ${index + 1}`;
          image.loading = index === this.currentIndex ? 'eager' : 'lazy';
          
          this.setupImageZoom(image, slide);
          
          slide.appendChild(image);
        }
      }
      
      this.slider.appendChild(slide);
    });
  }

  // NEW: Smart detection - reads what the PDP actually loaded
  detectPDPImageSize(pdpImageElement) {
    // Get the actual URL the browser chose from srcset
    const actualSrc = pdpImageElement.currentSrc || pdpImageElement.src;
    
    // If we can read it, use it exactly
    if (actualSrc) {
      console.log(`Smart detection: Using PDP size from ${actualSrc}`);
      return actualSrc;
    }
    
    // Fallback: use 1100px if detection fails
    console.log('Smart detection failed, falling back to 1100px');
    return this.getLowResUrl(pdpImageElement.src);
  }

  // FALLBACK: If smart detection fails, default to 1100px
  getLowResUrl(src) {
    if (src.includes('width=')) {
      return src.replace(/width=\d+/, 'width=1100');
    }
    return src + (src.includes('?') ? '&' : '?') + 'width=1100';
  }

  // HIGH-RES: Always 2048px for zoom detail
  getHighResUrl(src) {
    if (src.includes('width=')) {
      return src.replace(/width=\d+/, 'width=2048');
    }
    return src + (src.includes('?') ? '&' : '?') + 'width=2048';
  }

  upgradeToHighRes(slideIndex, attempt = 1) {
    if (this.highResLoaded.has(slideIndex)) return;
    
    const slide = this.slider?.children[slideIndex];
    if (!slide) return;
    
    const image = slide.querySelector('.mobile-gallery-image:not(.video-poster)');
    if (!image || !image.dataset.highResSrc) return;
    
    const highResSrc = image.dataset.highResSrc;
    const tempImg = new Image();
    
    tempImg.onload = () => {
      image.src = highResSrc;
      this.highResLoaded.add(slideIndex);
      console.log(`High-res loaded for slide ${slideIndex + 1}`);
    };
    
    tempImg.onerror = () => {
      if (attempt < 3) {
        console.log(`High-res failed for slide ${slideIndex + 1}, attempt ${attempt}. Retrying...`);
        setTimeout(() => {
          this.upgradeToHighRes(slideIndex, attempt + 1);
        }, attempt * 1000);
      } else {
        console.log(`High-res failed for slide ${slideIndex + 1} after 3 attempts. Staying on low-res.`);
      }
    };
    
    tempImg.src = highResSrc;
  }

  startPreloadQueue(currentIndex) {
    const queue = [currentIndex];
    for (let i = 1; i <= 3; i++) {
      if (currentIndex + i < this.mediaContainers.length) {
        queue.push(currentIndex + i);
      }
      if (currentIndex - i >= 0) {
        queue.push(currentIndex - i);
      }
    }
    
    this.preloadQueue = queue;
    this.processPreloadQueue();
  }

  processPreloadQueue() {
    if (this.isPreloading || this.preloadQueue.length === 0) return;
    
    this.isPreloading = true;
    const slideIndex = this.preloadQueue.shift();
    
    if (!this.highResLoaded.has(slideIndex)) {
      this.upgradeToHighRes(slideIndex);
    }
    
    setTimeout(() => {
      this.isPreloading = false;
      this.processPreloadQueue();
    }, 200);
  }

  storeVideoData(container, slide) {
    const videoElement = container.querySelector('deferred-media, external-media');
    if (!videoElement) return;
    
    const template = videoElement.querySelector('template');
    const autoplay = videoElement.dataset.autoplay === 'true';
    
    slide.dataset.videoTemplate = template ? template.innerHTML : '';
    slide.dataset.autoplay = autoplay;
    slide.dataset.mediaId = videoElement.querySelector('[id*="Deferred-Media"]')?.id || '';
  }

  createLazyVideo(slide) {
    // Removed: if (window.innerWidth >= 768) return; — was blocking video on tablet/desktop
    if (slide.dataset.videoCreated === 'true') return;
    
    const template = slide.dataset.videoTemplate;
    const autoplay = slide.dataset.autoplay === 'true';
    const videoIndex = parseInt(slide.dataset.videoIndex) || 0;
    
    if (!template) return;
    
    this.trackEvent('Mobile Gallery Video Loaded', {
      slideIndex: videoIndex + 1,
      autoplay: autoplay,
      sessionDuration: this.getSessionDuration()
    });
    
    const videoContainer = document.createElement('div');
    videoContainer.className = 'mobile-gallery-video';
    videoContainer.innerHTML = template;
    
    const video = videoContainer.querySelector('video, iframe');
    if (video) {
      video.style.maxWidth = '95vw';
      video.style.maxHeight = '75vh';
      video.style.width = 'auto';
      video.style.height = 'auto';
      video.style.objectFit = 'contain';
      
      if (video.tagName === 'VIDEO') {
        video.addEventListener('play', () => {
          this.trackEvent('Mobile Gallery Video Play', {
            slideIndex: videoIndex + 1,
            sessionDuration: this.getSessionDuration()
          });
        });
        
        video.addEventListener('pause', () => {
          this.trackEvent('Mobile Gallery Video Pause', {
            slideIndex: videoIndex + 1,
            currentTime: video.currentTime,
            duration: video.duration,
            sessionDuration: this.getSessionDuration()
          });
        });
        
        video.addEventListener('ended', () => {
          this.trackEvent('Mobile Gallery Video Ended', {
            slideIndex: videoIndex + 1,
            duration: video.duration,
            sessionDuration: this.getSessionDuration()
          });
        });
      }
    }
    
    const poster = slide.querySelector('.video-poster');
    if (poster) {
      slide.removeChild(poster);
    }
    
    slide.appendChild(videoContainer);
    slide.dataset.videoCreated = 'true';
    
    if (video && autoplay) {
      this.setupVideoAutoplay(video, slide);
    }
  }

  setupVideoAutoplay(video, slide) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
          if (video.tagName === 'VIDEO') {
            video.play().catch(() => {});
          } else if (video.tagName === 'IFRAME') {
            video.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
          }
        } else {
          if (video.tagName === 'VIDEO') {
            video.pause();
          }
        }
      });
    }, { threshold: 0.5 });
    
    observer.observe(slide);
    slide._videoObserver = observer;
  }

  destroyLazyVideo(slide) {
    if (slide.dataset.videoCreated !== 'true') return;
    
    if (slide._videoObserver) {
      slide._videoObserver.disconnect();
      delete slide._videoObserver;
    }
    
    const videoContainer = slide.querySelector('.mobile-gallery-video');
    if (videoContainer) {
      slide.removeChild(videoContainer);
    }
    
    slide.dataset.videoCreated = 'false';
  }

  setupImageZoom(image, slide) {
    // CUSTOM ZOOM: JS-based zoom that transforms image inside fixed container
    // This prevents Safari viewport recalculation issues
    
    const self = this;
    let scale = 1;
    let lastScale = 1;
    let panX = 0;
    let panY = 0;
    let lastPanX = 0;
    let lastPanY = 0;
    let isPinching = false;
    let isPanning = false;
    let initialPinchDistance = 0;
    let lastTouchX = 0;
    let lastTouchY = 0;
    
    // For zoom-to-point calculation
    let pinchCenterX = 0;
    let pinchCenterY = 0;
    let imageRect = null;
    
    const MIN_SCALE = 1;
    const MAX_SCALE = 3;
    
    // Apply transform to image
    const applyTransform = () => {
      image.style.transform = `translate(${panX}px, ${panY}px) scale(${scale})`;
      slide.dataset.isZoomed = scale > 1.05 ? 'true' : 'false';
    };
    
    // Calculate distance between two touch points
    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };
    
    // Calculate midpoint between two touch points
    const getMidpoint = (touches) => {
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };
    
    // Get pinch center relative to image center
    const getPinchCenterRelativeToImage = (touches) => {
      const midpoint = getMidpoint(touches);
      const rect = image.getBoundingClientRect();
      
      // Get center of image
      const imageCenterX = rect.left + rect.width / 2;
      const imageCenterY = rect.top + rect.height / 2;
      
      // Return offset from image center
      return {
        x: midpoint.x - imageCenterX,
        y: midpoint.y - imageCenterY
      };
    };
    
    // Constrain pan so image doesn't go too far off-screen
    const constrainPan = () => {
      if (scale <= 1) {
        panX = 0;
        panY = 0;
        return;
      }
      
      // Get the original (unscaled) image dimensions
      const rect = image.getBoundingClientRect();
      const containerRect = slide.getBoundingClientRect();
      
      // Calculate max pan based on how much larger the scaled image is
      const scaledWidth = (rect.width / scale) * scale; // Current visible width
      const scaledHeight = (rect.height / scale) * scale;
      
      const maxPanX = Math.max(0, (scaledWidth - containerRect.width) / 2);
      const maxPanY = Math.max(0, (scaledHeight - containerRect.height) / 2);
      
      panX = Math.max(-maxPanX, Math.min(maxPanX, panX));
      panY = Math.max(-maxPanY, Math.min(maxPanY, panY));
    };
    
    // Reset zoom state
    slide._resetZoom = () => {
      if (scale > 1.05) {
        self.trackEvent('Mobile Gallery Zoom Reset', {
          slideIndex: self.currentIndex + 1,
          method: 'swipe_navigation',
          sessionDuration: self.getSessionDuration()
        });
      }
      scale = 1;
      lastScale = 1;
      panX = 0;
      panY = 0;
      lastPanX = 0;
      lastPanY = 0;
      image.style.transform = '';
      image.style.transformOrigin = 'center center';
      slide.dataset.isZoomed = 'false';
    };
    
    // Touch start handler
    image.addEventListener('touchstart', (e) => {
      if (e.touches.length === 2) {
        // Pinch start
        e.preventDefault();
        e.stopPropagation(); // Prevent slider from seeing this
        isPinching = true;
        isPanning = false;
        initialPinchDistance = getDistance(e.touches);
        lastScale = scale;
        lastPanX = panX;
        lastPanY = panY;
        
        // Store the pinch center relative to image for zoom-to-point
        const pinchCenter = getPinchCenterRelativeToImage(e.touches);
        pinchCenterX = pinchCenter.x;
        pinchCenterY = pinchCenter.y;
        
        self.trackEvent('Mobile Gallery Zoom Start', {
          slideIndex: self.currentIndex + 1,
          sessionDuration: self.getSessionDuration()
        });
        
      } else if (e.touches.length === 1) {
        // Potential pan start (only if zoomed)
        if (scale > 1.05) {
          e.stopPropagation(); // Prevent slider from seeing this when zoomed
          isPanning = true;
          lastTouchX = e.touches[0].clientX;
          lastTouchY = e.touches[0].clientY;
          lastPanX = panX;
          lastPanY = panY;
        }
        // If not zoomed, let the event bubble up to slider for swipe handling
      }
    }, { passive: false });
    
    // Touch move handler
    image.addEventListener('touchmove', (e) => {
      if (isPinching && e.touches.length === 2) {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate new scale
        const currentDistance = getDistance(e.touches);
        const scaleChange = currentDistance / initialPinchDistance;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, lastScale * scaleChange));
        
        // ZOOM-OUT FIX: Different pan logic for zooming in vs out
        if (newScale < lastScale) {
          // Zooming OUT — proportionally reduce pan toward center
          // This prevents the diagonal "fly off" effect
          const scaleRatio = newScale / lastScale;
          panX = lastPanX * scaleRatio;
          panY = lastPanY * scaleRatio;
        } else {
          // Zooming IN — use zoom-to-point logic
          // Adjust pan to keep pinch center stationary
          const scaleDiff = newScale - lastScale;
          panX = lastPanX - (pinchCenterX * scaleDiff);
          panY = lastPanY - (pinchCenterY * scaleDiff);
        }
        
        scale = newScale;
        
        // Track zoom in/out events
        if (scale > 1.05 && lastScale <= 1.05) {
          self.trackEvent('Mobile Gallery Zoomed In', {
            slideIndex: self.currentIndex + 1,
            zoomLevel: scale,
            sessionDuration: self.getSessionDuration()
          });
        }
        
        applyTransform();
        
      } else if (isPanning && e.touches.length === 1 && scale > 1.05) {
        e.preventDefault();
        e.stopPropagation();
        
        // Calculate pan delta
        const touchX = e.touches[0].clientX;
        const touchY = e.touches[0].clientY;
        const deltaX = touchX - lastTouchX;
        const deltaY = touchY - lastTouchY;
        
        panX = lastPanX + deltaX;
        panY = lastPanY + deltaY;
        
        constrainPan();
        applyTransform();
      }
    }, { passive: false });
    
    // Touch end handler
    image.addEventListener('touchend', (e) => {
      if (isPinching) {
        isPinching = false;
        
        // Snap to 1x if close
        if (scale < 1.1) {
          scale = 1;
          panX = 0;
          panY = 0;
          
          self.trackEvent('Mobile Gallery Zoomed Out', {
            slideIndex: self.currentIndex + 1,
            sessionDuration: self.getSessionDuration()
          });
        }
        
        constrainPan();
        applyTransform();
        lastScale = scale;
        lastPanX = panX;
        lastPanY = panY;
      }
      
      if (isPanning) {
        isPanning = false;
        lastPanX = panX;
        lastPanY = panY;
      }
    }, { passive: true });
    
    // Prevent default touch action on image
    image.style.touchAction = 'none';
    image.style.transformOrigin = 'center center';
    image.style.willChange = 'transform';
    
    // Initialize
    slide.dataset.isZoomed = 'false';
  }

  resetZoomState(imageElement) {
    imageElement.style.touchAction = 'manipulation';
  }

  getThumbnailUrl(src) {
    if (src.includes('width=')) {
      return src.replace(/width=\d+/, 'width=128');
    }
    return src + (src.includes('?') ? '&' : '?') + 'width=128';
  }

  populateFilmstrip() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';
    
    let html = '<div class="filmstrip-wrapper">';
    this.mediaContainers.forEach((container, index) => {
      const img = container.querySelector('img');
      const thumbUrl = img ? this.getThumbnailUrl(img.src) : '';
      
      html += `
        <div class="filmstrip-item ${index === this.currentIndex ? 'active' : ''}" 
             data-index="${index}">
          <img src="${thumbUrl}" alt="View ${index + 1}" loading="lazy">
        </div>
      `;
    });
    
    // ClassyTea: Only add spacer on mobile (horizontal filmstrip)
    if (!this.isDesktopLayout) {
      html += `
        <div class="filmstrip-spacer" style="
          width: 48px; 
          height: 72px; 
          min-width: 48px; 
          background: black; 
          flex-shrink: 0;
          border: 0.25px solid rgba(255, 255, 255, 0.15);
          border-radius: 1px;
        "></div>
      `;
    }
    
    html += '</div>';
    
    this.dotsContainer.innerHTML = html;
    
    // ClassyTea: Move ATC button inside sidebar on desktop
    this.repositionATCButton();
    
    this.dotsContainer.querySelectorAll('.filmstrip-item').forEach((item, index) => {
      item.addEventListener('click', () => {
        this.trackEvent('Mobile Gallery Navigation', {
          method: 'filmstrip_click',
          direction: index > this.currentIndex ? 'next' : 'previous',
          fromSlide: this.currentIndex + 1,
          toSlide: index + 1,
          sessionDuration: this.getSessionDuration()
        });

        if (window.LO && window.LO.events) {
          try {
            LO.events.track('Mobile Gallery Thumbnail Click', {
              fromSlide: this.currentIndex + 1,
              toSlide: index + 1,
              jumpDistance: Math.abs(index - this.currentIndex)
            });
          } catch (error) {
            console.warn('Lucky Orange thumbnail tracking error:', error);
          }
        }
        
        this.goTo(index);
      });
    });
  }

  // ClassyTea: Move ATC button into sidebar (desktop) or back to overlay (mobile)
  repositionATCButton() {
    const atcButton = document.getElementById('galleryATC');
    if (!atcButton) return;
    
    if (this.isDesktopLayout) {
      // Desktop: Move ATC inside the dots container (sidebar)
      if (atcButton.parentElement !== this.dotsContainer) {
        this.dotsContainer.appendChild(atcButton);
      }
    } else {
      // Mobile: Move ATC back to overlay (if not already there)
      if (atcButton.parentElement !== this.overlay) {
        this.overlay.appendChild(atcButton);
      }
    }
  }

  updateCounter() {
    if (this.counter) {
      this.counter.textContent = `${this.currentIndex + 1} / ${this.mediaContainers.length}`;
      this.counter.setAttribute('aria-label', `Image ${this.currentIndex + 1} of ${this.mediaContainers.length}`);
    }
  }

  updatePosition() {
    if (!this.slider) return;
    
    if (this.currentSlideStartTime) {
      const viewTime = Date.now() - this.currentSlideStartTime;
      this.slideViewTimes.set(this.currentIndex, 
        (this.slideViewTimes.get(this.currentIndex) || 0) + viewTime);
    }
    
    const slides = this.slider.querySelectorAll('.mobile-gallery-slide');
    slides.forEach(slide => {
      if (slide._resetZoom) {
        slide._resetZoom();
      }
    });
    
    // SAFARI FIX: Always use percentage, not vw units
    const translateX = -this.currentIndex * 100;
    this.slider.style.transform = `translateX(${translateX}%)`;
    
    this.dotsContainer?.querySelectorAll('.filmstrip-item').forEach((item, index) => {
      item.classList.toggle('active', index === this.currentIndex);
    });

    const activeThumb = this.dotsContainer?.querySelector('.filmstrip-item.active');
    if (activeThumb) {
      activeThumb.scrollIntoView({ 
        behavior: 'smooth', 
        inline: 'center',
        block: 'nearest'
      });
    }
    
    this.updateCounter();
    this.currentSlideStartTime = Date.now();
    
    this.handleVideoLazyLoading();
    if (this.updateArrowStates) this.updateArrowStates();
    
    this.upgradeToHighRes(this.currentIndex);
    this.startPreloadQueue(this.currentIndex);
    
    const hasVideo = this.mediaContainers[this.currentIndex]?.querySelector('deferred-media, [class*="deferred-media"], external-media');
    this.trackEvent('Mobile Gallery Slide Viewed', {
      slideIndex: this.currentIndex + 1,
      slideType: hasVideo ? 'video' : 'image',
      totalSlides: this.mediaContainers.length,
      sessionDuration: this.getSessionDuration()
    });
  }

  handleVideoLazyLoading() {
    const slides = this.slider.querySelectorAll('.mobile-gallery-slide');
    
    slides.forEach((slide, index) => {
      const hasVideo = slide.dataset.hasVideo === 'true';
      if (!hasVideo) return;
      
      if (Math.abs(index - this.currentIndex) <= 1) {
        this.createLazyVideo(slide);
      } else {
        this.destroyLazyVideo(slide);
      }
    });
  }

  next() {
    if (this.currentIndex < this.mediaContainers.length - 1) {
      this.currentIndex++;
      this.updatePosition();
    }
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
      this.updatePosition();
    }
  }

  goTo(index) {
    if (index >= 0 && index < this.mediaContainers.length) {
      this.currentIndex = index;
      this.updatePosition();
    }
  }

  handleTouchStart(e) {
    // Don't handle slider touch if current slide is zoomed
    const currentSlide = this.slider?.children[this.currentIndex];
    if (currentSlide?.dataset.isZoomed === 'true') {
      return; // Let the image zoom handler deal with it
    }
    
    this.touchStartX = e.touches[0].clientX;
    this.touchStartY = e.touches[0].clientY;
    this.touchCount = e.touches.length;
    this.startY = e.touches[0].clientY;
    this.isDragging = false;
    this.currentY = 0;
    
    // Track if touch started in dismiss zone (bottom 20% of modal)
    // You can adjust this value: 0.20 = bottom 20%, 0.25 = bottom 25%, etc.
    const dismissZoneThreshold = 0.20;
    const modalHeight = this.overlay.offsetHeight;
    const dismissZoneTop = modalHeight * (1 - dismissZoneThreshold);
    this.inDismissZone = this.touchStartY >= dismissZoneTop;
  }

  handleTouchMove(e) {
    // Don't handle slider touch if current slide is zoomed
    const currentSlide = this.slider?.children[this.currentIndex];
    if (currentSlide?.dataset.isZoomed === 'true') {
      return; // Let the image zoom handler deal with it
    }
    
    if (this.touchCount !== 1) return;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = Math.abs(currentX - this.touchStartX);
    const deltaY = currentY - this.startY;
    
    // Only allow drag-to-dismiss if touch started in the dismiss zone (bottom 25%)
    if (this.inDismissZone && deltaY < -20 && deltaX < Math.abs(deltaY) * 0.5) {
      this.isDragging = true;
      this.currentY = Math.abs(deltaY);
      this.updateModalPosition();
      e.preventDefault();
    }
  }

  handleTouchEnd(e) {
    // Don't handle slider touch if current slide is zoomed
    const currentSlide = this.slider?.children[this.currentIndex];
    if (currentSlide?.dataset.isZoomed === 'true') {
      return; // Let the image zoom handler deal with it
    }
    
    if (this.touchCount === 1 && e.touches.length === 0) {
      this.touchEndX = e.changedTouches[0].clientX;
      this.touchEndY = e.changedTouches[0].clientY;
      if (this.isDragging) {
        this.handleDismissEnd();
      } else {
        this.handleSwipe();
      }
    }
    this.isDragging = false;
    this.currentY = 0;
  }

  updateModalPosition() {
    if (!this.overlay) return;
    const maxDistance = window.innerHeight * 0.5;
    const progress = Math.min(this.currentY / maxDistance, 1);
    const translateY = -this.currentY;
    this.overlay.style.transform = `translateY(${translateY}px)`;
    if (progress > 0.9) {
      const fadeProgress = (progress - 0.9) / 0.1;
      const opacity = 1 - fadeProgress;
      this.overlay.style.opacity = opacity;
    }
  }

  handleDismissEnd() {
    const dismissThreshold = window.innerHeight * 0.2;
    if (this.currentY > dismissThreshold) {
      this.trackEvent('Mobile Gallery Closed', {
        method: 'swipe_dismiss',
        currentSlide: this.currentIndex + 1,
        totalSlides: this.mediaContainers.length,
        sessionDuration: this.getSessionDuration(),
        swipeDistance: this.currentY
      });
      this.animateDismiss();
    } else {
      this.animateSnapBack();
    }
  }

  animateDismiss() {
    if (!this.overlay) return;
    this.overlay.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
    this.overlay.style.transform = 'translateY(-100vh)';
    this.overlay.style.opacity = '0';
    setTimeout(() => {
      this.close();
      this.overlay.style.transition = '';
    }, 300);
  }

  animateSnapBack() {
    if (!this.overlay) return;
    this.overlay.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
    this.overlay.style.transform = 'translateY(0)';
    this.overlay.style.opacity = '1';
    setTimeout(() => {
      this.overlay.style.transition = '';
    }, 300);
  }

  handleSwipe() {
    const currentSlide = this.slider?.children[this.currentIndex];
    const isZoomed = currentSlide?.dataset.isZoomed === 'true';
    
    const swipeDistanceX = this.touchStartX - this.touchEndX;
    const swipeDistanceY = Math.abs(this.touchStartY - this.touchEndY);
    const minSwipeDistance = 75;
    
    if (Math.abs(swipeDistanceX) > minSwipeDistance && swipeDistanceY < 100) {
      
      if (isZoomed) {
        if (currentSlide._resetZoom) {
          currentSlide._resetZoom();
        }
        return;
      }
      
      const direction = swipeDistanceX > 0 ? 'next' : 'previous';
      const fromSlide = this.currentIndex + 1;
      let toSlide = fromSlide;
      
      if (direction === 'next' && this.currentIndex < this.mediaContainers.length - 1) {
        toSlide = fromSlide + 1;
        this.trackEvent('Mobile Gallery Navigation', {
          method: 'swipe',
          direction: direction,
          fromSlide: fromSlide,
          toSlide: toSlide,
          swipeDistance: Math.abs(swipeDistanceX),
          sessionDuration: this.getSessionDuration()
        });
        this.next();
      } else if (direction === 'previous' && this.currentIndex > 0) {
        toSlide = fromSlide - 1;
        this.trackEvent('Mobile Gallery Navigation', {
          method: 'swipe',
          direction: direction,
          fromSlide: fromSlide,
          toSlide: toSlide,
          swipeDistance: Math.abs(swipeDistanceX),
          sessionDuration: this.getSessionDuration()
        });
        this.prev();
      } else {
        this.trackEvent('Mobile Gallery Swipe Boundary', {
          direction: direction,
          currentSlide: fromSlide,
          totalSlides: this.mediaContainers.length,
          swipeDistance: Math.abs(swipeDistanceX),
          sessionDuration: this.getSessionDuration()
        });
      }
    }
  }

  createNavArrows() {
    if (!this.overlay) return;
    
    const existingArrows = this.overlay.querySelectorAll('.mobile-gallery-nav');
    existingArrows.forEach(arrow => arrow.remove());
    
    const prevArrow = document.createElement('button');
    prevArrow.className = 'mobile-gallery-nav mobile-gallery-nav--prev';
    prevArrow.addEventListener('click', (e) => {
      e.stopPropagation();
      this.prev();
    });
    
    const nextArrow = document.createElement('button');
    nextArrow.className = 'mobile-gallery-nav mobile-gallery-nav--next';
    nextArrow.addEventListener('click', (e) => {
      e.stopPropagation();
      this.next();
    });
    
    this.overlay.appendChild(prevArrow);
    this.overlay.appendChild(nextArrow);
    
    this.prevArrow = prevArrow;
    this.nextArrow = nextArrow;
    
    this.updateArrowStates();
  }

  updateArrowStates() {
    if (!this.prevArrow || !this.nextArrow) return;
    
    if (this.currentIndex === 0) {
      this.prevArrow.classList.add('mobile-gallery-nav--disabled');
    } else {
      this.prevArrow.classList.remove('mobile-gallery-nav--disabled');
    }
    
    if (this.currentIndex === this.mediaContainers.length - 1) {
      this.nextArrow.classList.add('mobile-gallery-nav--disabled');
    } else {
      this.nextArrow.classList.remove('mobile-gallery-nav--disabled');
    }
  }

  showZoomHint() {
    const hint = document.createElement('div');
    hint.className = 'mobile-gallery-zoom-hint';
    hint.textContent = 'PINCH TO ZOOM';
    
    this.overlay.appendChild(hint);
    
    setTimeout(() => {
      hint.classList.add('show');
    }, 500);
    
    setTimeout(() => {
      hint.classList.remove('show');
      setTimeout(() => {
        if (hint.parentNode) {
          hint.parentNode.removeChild(hint);
        }
      }, 400);
    }, 3000);
  }
}

// ClassyTea: Initialize on all screen sizes (removed width restriction)
document.addEventListener('DOMContentLoaded', () => {
  new MobileGallery();
});

document.addEventListener('DOMContentLoaded', function() {
  const conditionCheckbox = document.getElementById('condition-modal');
  const conditionOverlay = document.querySelector('.ct-modal-overlay');
  let conditionHistoryAdded = false;

  if (conditionCheckbox) {
    conditionCheckbox.addEventListener('change', function() {
      if (this.checked && !conditionHistoryAdded) {
        history.pushState({ conditionModalOpen: true }, '', window.location.href);
        conditionHistoryAdded = true;
      } else if (!this.checked) {
        const content = conditionOverlay?.querySelector('.ct-modal-content');
        if (content) {
          content.style.transform = '';
        }
      }
    });

    window.addEventListener('popstate', function(e) {
      if (conditionCheckbox.checked && !e.state?.conditionModalOpen) {
        conditionCheckbox.checked = false;
        conditionHistoryAdded = false;
      }
    });
  }

  if (conditionOverlay) {
    let startY = 0;
    let currentY = 0;
    
    conditionOverlay.addEventListener('touchstart', (e) => {
      startY = e.touches[0].clientY;
    }, { passive: true });
    
    conditionOverlay.addEventListener('touchmove', (e) => {
      currentY = e.touches[0].clientY - startY;
      if (currentY > 0) {
        e.preventDefault();
        const content = conditionOverlay.querySelector('.ct-modal-content');
        if (content) {
          content.style.transform = `translateY(${Math.min(currentY, 200)}px)`;
        }
      }
    }, { passive: false });
    
    conditionOverlay.addEventListener('touchend', () => {
      if (currentY > 80) {
        conditionCheckbox.checked = false;
      } else {
        const content = conditionOverlay.querySelector('.ct-modal-content');
        if (content) {
          content.style.transform = 'translateY(0)';
        }
      }
      currentY = 0;
    }, { passive: true });
  }
});

document.addEventListener('DOMContentLoaded', function() {
  const zoomIcon = document.getElementById('zoom-hint-icon');
  if (zoomIcon) {
    zoomIcon.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      
      setTimeout(() => {
        console.log('========== COMPREHENSIVE DEBUG ==========');
        console.log('TIMESTAMP:', new Date().toISOString());
        
        // METHOD 1: Counter text
        const counter = document.querySelector('.slider-counter--current');
        const counterText = counter ? counter.textContent : 'NULL';
        const counterIndex = counter ? parseInt(counterText) - 1 : -1;
        console.log('METHOD 1 - Counter text:', counterText, '→ Index:', counterIndex);
        
        // METHOD 2: All aria-hidden states
        const allSlides = document.querySelectorAll('.slideshow__slide');
        console.log('METHOD 2 - Total slideshow slides:', allSlides.length);
        
        const visibleSlides = [];
        allSlides.forEach((slide, idx) => {
          const ariaHidden = slide.getAttribute('aria-hidden');
          const dataIndex = slide.dataset.index;
          if (ariaHidden === 'false') {
            visibleSlides.push({slideIdx: idx, dataIndex: dataIndex});
            console.log(`  ✓ VISIBLE: Slide ${idx}, data-index=${dataIndex}, aria-hidden=${ariaHidden}`);
          }
        });
        console.log('Total visible slides (aria-hidden=false):', visibleSlides.length);
        
        // METHOD 3: Check if there's a "current" class
        const currentSlide = document.querySelector('.slideshow__slide.current, .slideshow__slide--current, .slideshow__slide[data-current="true"]');
        console.log('METHOD 3 - Slide with .current class:', currentSlide ? currentSlide.dataset.index : 'NULL');
        
        // METHOD 4: Check transform/position
        const sliderTrack = document.querySelector('.slideshow__slides, .slider-track');
        if (sliderTrack) {
          const transform = sliderTrack.style.transform;
          console.log('METHOD 4 - Slider transform:', transform);
        }
        
        // Check our JavaScript's filtered containers
        const mobileGalleryInstance = window.mobileGalleryInstance;
        if (mobileGalleryInstance) {
          console.log('JavaScript filtered containers:', mobileGalleryInstance.mediaContainers.length);
        }
        
        // DECISION LOGIC
        let finalIndex = 0;
        let method = 'default';
        
        if (visibleSlides.length === 1) {
          finalIndex = parseInt(visibleSlides[0].dataIndex);
          method = 'aria-hidden (single visible)';
        } else if (visibleSlides.length > 1) {
          // Multiple visible - use the LAST one (transition destination)
          finalIndex = parseInt(visibleSlides[visibleSlides.length - 1].dataIndex);
          method = 'aria-hidden (last of multiple - transition destination)';
          console.log('INFO: Multiple visible slides during transition, using last (destination)');
        } else {
          // No visible slides found via aria - use counter
          finalIndex = counterIndex;
          method = 'counter fallback';
        }
        
        console.log('FINAL DECISION:', method, '→ Opening at index:', finalIndex);
        console.log('========== END DEBUG ==========');
        
        document.dispatchEvent(new CustomEvent('openMobileGallery', { 
          detail: { startIndex: finalIndex }
        }));
      }, 100);
    });
  }
});